import React, { useState, useEffect, useMemo } from 'react';
import { adminAPI } from '../../services/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Loader2, Save, Pencil, Plus, Trash2, Server, Database, HardDrive, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const CURRENCIES   = ['GBP', 'EUR', 'USD', 'CAD', 'AUD'];
const CURR_SYMBOLS = { GBP: '\u00a3', EUR: '\u20ac', USD: '$', CAD: 'CA$', AUD: 'A$' };

const APP_SERVER_REGIONS = [
  { code: 'us-east-1',      name: 'US East' },
  { code: 'eu-west-1',      name: 'EU (Ireland)' },
  { code: 'ca-central-1',   name: 'Canada' },
  { code: 'eu-central-1',   name: 'Frankfurt' },
  { code: 'af-south-1',     name: 'Cape Town' },
  { code: 'me-central-1',   name: 'UAE' },
  { code: 'ap-southeast-2', name: 'Sydney' },
  { code: 'eu-west-2',      name: 'London' },
  { code: 'ap-southeast-1', name: 'Singapore' },
];
const NAVY   = 'hsl(var(--c4p-navy-900))';
const ORANGE = 'hsl(var(--c4p-orange-600))';

// ── Shared price-edit helper ──────────────────────────────────────────────────
function PriceDialog({ title, subtitle, prices, onChange, onSave, onClose, saving }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-display)' }}>{title}</DialogTitle></DialogHeader>
        {subtitle && <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{subtitle}</p>}
        <div className="space-y-3 py-1">
          {CURRENCIES.map(c => (
            <div key={c} className="flex items-center gap-3">
              <span className="inline-block w-16 text-sm font-semibold text-right flex-shrink-0">{CURR_SYMBOLS[c]} {c}</span>
              <Input
                type="number" min="0" step="0.01"
                value={prices[c] ?? ''}
                onChange={e => onChange(c, parseFloat(e.target.value) || 0)}
                className="flex-1 tabular-nums"
              />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} style={{ background: NAVY, color: 'white' }}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={14} />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Standard Pricing Tab ──────────────────────────────────────────────────────
function StandardTab() {
  const [data, setData] = useState(null);
  const [prices, setPrices] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminAPI.getStandardPricing().then(r => { setData(r.data); setPrices(r.data?.prices_by_currency || {}); })
      .catch(() => toast.error('Failed to load'));
  }, []);

  const save = async () => {
    setSaving(true);
    try { await adminAPI.updateStandardPricing({ prices_by_currency: prices }); toast.success('Saved'); }
    catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="py-8"><Skeleton className="h-32 w-full" /></div>;
  return (
    <div className="space-y-5" data-testid="admin-pricing-standard-table">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold" style={{ fontFamily: 'var(--font-display)' }}>C4P Standard - Per-User-Per-Month</h3>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>Monthly fee per user. No DB or App Server included.</p>
        </div>
        <Button onClick={save} disabled={saving} style={{ background: NAVY, color: 'white' }} className="gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={14} />} Save
        </Button>
      </div>
      <div className="rounded-xl border bg-white overflow-hidden">
        <Table>
          <TableHeader><TableRow style={{ background: 'hsl(var(--muted))' }}>
            <TableHead>Currency</TableHead>
            <TableHead>PUPM (× users = monthly base)</TableHead>
            <TableHead style={{ color: 'hsl(var(--muted-fg))' }}>Example: 10 users</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {CURRENCIES.map(c => (
              <TableRow key={c}>
                <TableCell className="font-medium">{CURR_SYMBOLS[c]} {c}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <span className="inline-block w-8 text-sm text-right flex-shrink-0" style={{ color: 'hsl(var(--muted-fg))' }}>{CURR_SYMBOLS[c]}</span>
                    <Input type="number" min="0" step="0.01" value={prices[c] ?? ''}
                      onChange={e => setPrices(p => ({ ...p, [c]: parseFloat(e.target.value) || 0 }))}
                      className="w-32 h-8 text-sm tabular-nums" data-testid={`standard-price-${c}`} />
                    <span className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>/user/mo</span>
                  </div>
                </TableCell>
                <TableCell className="text-sm tabular-nums" style={{ color: 'hsl(var(--muted-fg))' }}>
                  {CURR_SYMBOLS[c]}{((prices[c] || 0) * 10).toLocaleString()}/mo
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ── Advanced Tiers Tab ────────────────────────────────────────────────────────
const EMPTY_TIER = { tier_name: '', min_users: '', max_users: '', prices_by_currency: { GBP: '', EUR: '', USD: '', CAD: '', AUD: '' } };

function tierLabel(min, max) {
  return `Tier (${min}${max ? `–${max}` : '+'} users)`;
}

// Defined OUTSIDE AdvancedTiersTab so the component reference is stable -
// avoids focus loss caused by React remounting inputs on every keystroke.
function PriceFields({ prices, onChange }) {
  return (
    <div className="space-y-2">
      {CURRENCIES.map(c => (
        <div key={c} className="flex items-center gap-3">
          <span className="w-16 text-sm font-medium tabular-nums">{CURR_SYMBOLS[c]} {c}</span>
          <Input type="number" min="0" step="0.01" placeholder="0.00"
            value={prices[c] || ''}
            onChange={e => onChange(c, e.target.value)}
            className="w-36 tabular-nums" />
        </div>
      ))}
    </div>
  );
}

function BoundsFields({ min, max, onMin, onMax }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1">
        <Label>Min Users *</Label>
        <Input type="number" min="1" placeholder="e.g. 20" value={min || ''} onChange={e => onMin(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label>Max Users <span className="text-xs font-normal" style={{ color: 'hsl(var(--muted-fg))' }}>(blank = unlimited)</span></Label>
        <Input type="number" min="1" placeholder="e.g. 49 or blank" value={max || ''} onChange={e => onMax(e.target.value)} />
      </div>
    </div>
  );
}

function AdvancedTiersTab() {
  const [tiers, setTiers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editTier, setEditTier]   = useState(null);
  const [form, setForm]           = useState(EMPTY_TIER);
  const [saving, setSaving]       = useState(false);
  const [deleting, setDeleting]   = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addForm, setAddForm]     = useState({ ...EMPTY_TIER });
  const [adding, setAdding]       = useState(false);

  const load = () => adminAPI.getAdvancedTiers()
    .then(r => { setTiers(r.data || []); setLoading(false); })
    .catch(() => toast.error('Failed to load tiers'));
  useEffect(() => { load(); }, []);

  const openEdit = (t) => {
    setEditTier(t);
    // Strip zero values so the field starts blank rather than showing "0"
    const stripped = Object.fromEntries(
      Object.entries(t.prices_by_currency).map(([k, v]) => [k, v === 0 ? '' : String(v)])
    );
    setForm({ tier_name: t.tier_name, min_users: t.min_users, max_users: t.max_users ?? '', prices_by_currency: stripped });
  };

  const saveEdit = async () => {
    if (!form.min_users) return toast.error('Min users is required');
    setSaving(true);
    try {
      const autoName = tierLabel(form.min_users, form.max_users || null);
      await adminAPI.updateAdvancedTier(editTier.id, {
        tier_name: form.tier_name || autoName,
        prices_by_currency: Object.fromEntries(Object.entries(form.prices_by_currency).map(([k, v]) => [k, parseFloat(v) || 0])),
        min_users: parseInt(form.min_users),
        max_users: form.max_users ? parseInt(form.max_users) : null,
      });
      toast.success('Tier updated'); setEditTier(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (tier) => {
    setDeleting(tier.id);
    try {
      await adminAPI.deleteAdvancedTier(tier.id);
      toast.success(`${tier.tier_name} deleted`); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cannot delete this tier'); }
    finally { setDeleting(null); }
  };

  const handleAdd = async () => {
    if (!addForm.min_users) return toast.error('Min users is required');
    setAdding(true);
    try {
      const min = parseInt(addForm.min_users);
      const max = addForm.max_users ? parseInt(addForm.max_users) : null;
      await adminAPI.createAdvancedTier({
        tier_name: addForm.tier_name || tierLabel(min, max),
        min_users: min,
        max_users: max,
        prices_by_currency: Object.fromEntries(Object.entries(addForm.prices_by_currency).map(([k, v]) => [k, parseFloat(v) || 0])),
      });
      toast.success('Tier added'); setShowAddDialog(false); setAddForm({ ...EMPTY_TIER }); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add tier'); }
    finally { setAdding(false); }
  };

  if (loading) return <div className="py-8"><Skeleton className="h-40 w-full" /></div>;

  const sortedTiers = [...tiers].sort((a, b) => a.min_users - b.min_users);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold" style={{ fontFamily: 'var(--font-display)' }}>C4P Advanced &amp; MTPro - User Tier Pricing</h3>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
            The quote system selects the PUPM tier automatically based on total user count. Tiers must not overlap.
          </p>
        </div>
        <Button size="sm" onClick={() => { setAddForm({ ...EMPTY_TIER }); setShowAddDialog(true); }}
          style={{ background: 'hsl(var(--c4p-orange-600))', color: 'white' }} className="gap-1.5">
          <Plus size={13} /> Add Tier
        </Button>
      </div>

      <div className="rounded-xl border bg-white overflow-hidden">
        <ScrollArea className="w-full">
          <Table>
            <TableHeader>
              <TableRow style={{ background: 'hsl(var(--muted))' }}>
                <TableHead>Tier Name</TableHead>
                <TableHead>User Range</TableHead>
                {CURRENCIES.map(c => <TableHead key={c}>{CURR_SYMBOLS[c]} {c} / user / mo</TableHead>)}
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedTiers.length === 0 && (
                <TableRow><TableCell colSpan={CURRENCIES.length + 3} className="text-center py-8 text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>No tiers yet - add one above.</TableCell></TableRow>
              )}
              {sortedTiers.map(t => (
                <TableRow key={t.id} className="data-table-row">
                  <TableCell className="font-semibold">{t.tier_name}</TableCell>
                  <TableCell className="text-sm tabular-nums">
                    {t.min_users}
                    {t.max_users ? ` – ${t.max_users}` : '+'} users
                  </TableCell>
                  {CURRENCIES.map(c => (
                    <TableCell key={c} className="tabular-nums text-sm">
                      {CURR_SYMBOLS[c]}{Number(t.prices_by_currency?.[c] || 0) % 1 === 0
                        ? Math.round(t.prices_by_currency?.[c] || 0)
                        : (t.prices_by_currency?.[c] || 0).toFixed(2)}
                    </TableCell>
                  ))}
                  <TableCell>
                    <div className="flex items-center gap-0.5">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(t)}
                        data-testid={`edit-tier-${t.tier_number}`}><Pencil size={12} /></Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                        disabled={deleting === t.id || tiers.length <= 1}
                        style={{ color: tiers.length <= 1 ? 'hsl(var(--border))' : 'hsl(var(--danger))' }}
                        title={tiers.length <= 1 ? 'Cannot delete last tier' : 'Delete tier'}
                        onClick={() => handleDelete(t)}
                        data-testid={`delete-tier-${t.tier_number}`}>
                        {deleting === t.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
        Changes take effect immediately on new quotes. Existing saved quotes retain their snapshot pricing.
      </p>

      {/* Edit dialog */}
      {editTier && (
        <Dialog open onOpenChange={() => setEditTier(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Edit Tier</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1">
                <Label>Tier Name <span className="text-xs font-normal" style={{ color: 'hsl(var(--muted-fg))' }}>(auto-generated if left blank)</span></Label>
                <Input value={form.tier_name} placeholder={tierLabel(form.min_users, form.max_users)}
                  onChange={e => setForm(f => ({ ...f, tier_name: e.target.value }))} />
              </div>
              <BoundsFields
                min={form.min_users} max={form.max_users}
                onMin={v => setForm(f => ({ ...f, min_users: v }))}
                onMax={v => setForm(f => ({ ...f, max_users: v }))}
              />
              <div className="space-y-2"><Label>PUPM per Currency</Label>
                <PriceFields prices={form.prices_by_currency}
                  onChange={(c, v) => setForm(f => ({ ...f, prices_by_currency: { ...f.prices_by_currency, [c]: v } }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditTier(null)}>Cancel</Button>
              <Button onClick={saveEdit} disabled={saving} style={{ background: NAVY, color: 'white' }}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Add dialog */}
      {showAddDialog && (
        <Dialog open onOpenChange={() => setShowAddDialog(false)}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Add New Tier</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1">
                <Label>Tier Name <span className="text-xs font-normal" style={{ color: 'hsl(var(--muted-fg))' }}>(auto-generated if left blank)</span></Label>
                <Input value={addForm.tier_name} placeholder={addForm.min_users ? tierLabel(addForm.min_users, addForm.max_users) : 'e.g. Tier 4 (100+ users)'}
                  onChange={e => setAddForm(f => ({ ...f, tier_name: e.target.value }))} />
              </div>
              <BoundsFields
                min={addForm.min_users} max={addForm.max_users}
                onMin={v => setAddForm(f => ({ ...f, min_users: v }))}
                onMax={v => setAddForm(f => ({ ...f, max_users: v }))}
              />
              <div className="space-y-2"><Label>PUPM per Currency</Label>
                <PriceFields prices={addForm.prices_by_currency}
                  onChange={(c, v) => setAddForm(f => ({ ...f, prices_by_currency: { ...f.prices_by_currency, [c]: v } }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button onClick={handleAdd} disabled={adding} style={{ background: 'hsl(var(--c4p-orange-600))', color: 'white' }}>
                {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus size={13} /> Add Tier</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ── Server Catalogue Tab ────────────────────────────────────────────────────────
const DEFAULT_SERVER = { code: '', server_type: 'HANADB', description: '', aws_instance: '', os_included: '', cpu: 0, memory_gb: 0, included_data_gb: 0, included_backup_gb: 0, included_root_gb: 100, sort_order: 99 };

function ServerCatalogTab() {
  const [allServers, setAllServers]   = useState([]);
  const [tierBreaks, setTierBreaks]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [typeFilter, setTypeFilter]   = useState('HANADB');
  const [osFilter, setOsFilter]       = useState('Windows');
  const [showInactive, setShowInactive] = useState(false);
  // Edit prices
  const [editServer, setEditServer]           = useState(null);
  const [editPrices, setEditPrices]           = useState({});
  const [editMatrix, setEditMatrix]           = useState({});
  const [editPricingMode, setEditPricingMode] = useState('tiered'); // 'tiered' | 'static'
  const [editStaticPrices, setEditStaticPrices] = useState({ GBP: '', EUR: '', USD: '', CAD: '', AUD: '' });
  const [editStandalonePrices, setEditStandalonePrices] = useState({ GBP: '', EUR: '', USD: '', CAD: '', AUD: '' });
  const [editStandaloneMatrix, setEditStandaloneMatrix] = useState({});
  const [applyToVariants, setApplyToVariants] = useState(true);
  const [editDetails, setEditDetails]         = useState({});   // for Details tab
  const [saving, setSaving]           = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  // Add server
  const [showAdd, setShowAdd]         = useState(false);
  const [addForm, setAddForm]         = useState(DEFAULT_SERVER);
  const [addSaving, setAddSaving]     = useState(false);
  // Confirm dialogs
  const [toggleTarget, setToggleTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => {
    Promise.all([
      adminAPI.getServerCatalog({ include_inactive: true }),
      adminAPI.getAdvancedTiers(),
    ])
      .then(([sRes, tRes]) => { setAllServers(sRes.data || []); setTierBreaks(tRes.data || []); setLoading(false); })
      .catch(() => toast.error('Failed to load server catalog'));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line


  const visibleServers = useMemo(() => {
    let list = allServers.filter(s => s.server_type === typeFilter && !s.is_deleted);
    if (!showInactive) list = list.filter(s => s.is_active !== false);
    if (typeFilter !== 'Application') return list;
    return list.filter(s => {
      const os = s.os_included || '';
      if (osFilter === 'Windows')    return !os.includes('Linux') && !os.includes('Ubuntu') && !os.includes('GPU');
      if (osFilter === 'Linux')      return os.includes('Linux') && !os.includes('GPU') && !os.includes('Ubuntu');
      if (osFilter === 'Ubuntu Pro') return os.includes('Ubuntu');
      if (osFilter === 'GPU')        return os.includes('GPU');
      return true;
    });
  }, [allServers, typeFilter, osFilter, showInactive]);

  const isDbServer = (s) => s.server_type === 'HANADB' || s.server_type === 'SQLDB' || s.server_type === 'PRO4GLDB';
  const isBase     = (s) => s.is_base_for_hana || s.is_base_for_sql;

  const openEdit = (svr) => {
    setEditServer(svr);
    const mode = svr.pricing_mode || 'tiered';
    setEditPricingMode(mode);
    if (isDbServer(svr)) {
      const existing = svr.price_matrix || {};
      const m = {};
      ['1', '2', '3'].forEach(t => {
        m[t] = { GBP: 0, EUR: 0, USD: 0, CAD: 0, AUD: 0, ...(existing[t] || {}) };
      });
      setEditMatrix(m);
      // Strip zeros so static price fields open blank
      const sp = Object.fromEntries(
        Object.entries(svr.prices_by_currency || {}).map(([k, v]) => [k, v === 0 ? '' : String(v)])
      );
      setEditStaticPrices({ GBP: '', EUR: '', USD: '', CAD: '', AUD: '', ...sp });
      // Standalone prices (flat fallback)
      const stp = Object.fromEntries(
        Object.entries(svr.standalone_prices_by_currency || {}).map(([k, v]) => [k, v === 0 ? '' : String(v)])
      );
      setEditStandalonePrices({ GBP: '', EUR: '', USD: '', CAD: '', AUD: '', ...stp });
      // Standalone region matrix
      const srm = {};
      APP_SERVER_REGIONS.forEach(r => {
        srm[r.code] = { GBP: 0, EUR: 0, USD: 0, CAD: 0, AUD: 0, ...(svr.standalone_region_price_matrix?.[r.code] || {}) };
      });
      setEditStandaloneMatrix(srm);
    } else if (svr.region_price_matrix && Object.keys(svr.region_price_matrix).length > 0) {
      // App Server with region matrix - edit the region×currency matrix
      const m = {};
      APP_SERVER_REGIONS.forEach(r => {
        m[r.code] = { GBP: 0, EUR: 0, USD: 0, CAD: 0, AUD: 0, ...(svr.region_price_matrix[r.code] || {}) };
      });
      setEditMatrix(m);
    } else {
      setEditPrices({ GBP: 0, EUR: 0, USD: 0, CAD: 0, AUD: 0, ...(svr.prices_by_currency || {}) });
    }
    // Details state - copy all editable fields
    setEditDetails({
      code:                  svr.code,
      description:           svr.description,
      aws_instance:          svr.aws_instance || '',
      os_included:           svr.os_included || '',
      cpu:                   svr.cpu,
      memory_gb:             svr.memory_gb,
      included_data_gb:      svr.included_data_gb || 0,
      included_backup_gb:    svr.included_backup_gb || 0,
      included_root_gb:      svr.included_root_gb || 0,
      sql_server_included:   !!svr.sql_server_included,
      is_base_for_hana:      !!svr.is_base_for_hana,
      is_base_for_sql:       !!svr.is_base_for_sql,
      is_included_app_server: !!svr.is_included_app_server,
      sort_order:            svr.sort_order ?? 0,
    });
  };

  const saveDetails = async () => {
    if (!editDetails.code?.trim() || !editDetails.description?.trim()) {
      return toast.error('Code and Description are required');
    }
    setSavingDetails(true);
    try {
      await adminAPI.updateServerDetails(editServer.id, editDetails);
      toast.success('Server details updated');
      load();
      // Refresh editServer so pricing tab still works
      setEditServer(prev => ({ ...prev, ...editDetails }));
    } catch (ex) { toast.error(ex.response?.data?.detail || 'Failed to save'); }
    finally { setSavingDetails(false); }
  };

  const saveServer = async () => {
    setSaving(true);
    try {
      let payload;
      if (isDbServer(editServer)) {
        if (editPricingMode === 'static') {
          payload = {
            pricing_mode: 'static',
            prices_by_currency: Object.fromEntries(
              Object.entries(editStaticPrices).map(([k, v]) => [k, parseFloat(v) || 0])
            ),
            standalone_prices_by_currency: Object.fromEntries(
              Object.entries(editStandalonePrices).map(([k, v]) => [k, parseFloat(v) || 0])
            ),
            standalone_region_price_matrix: editStandaloneMatrix,
          };
        } else {
          payload = {
            pricing_mode: 'tiered',
            price_matrix: editMatrix,
            standalone_prices_by_currency: Object.fromEntries(
              Object.entries(editStandalonePrices).map(([k, v]) => [k, parseFloat(v) || 0])
            ),
            standalone_region_price_matrix: editStandaloneMatrix,
          };
        }
      } else if (editServer.region_price_matrix && Object.keys(editServer.region_price_matrix).length > 0) {
        payload = { region_price_matrix: editMatrix, apply_to_variants: applyToVariants };
      } else {
        payload = { prices_by_currency: editPrices, apply_to_variants: applyToVariants };
      }
      const result = await adminAPI.updateServerPrices(editServer.code, payload);
      const varNote = result.data?.variants_updated ? ` (+ ${result.data.variants_updated} OS variant${result.data.variants_updated > 1 ? 's' : ''})` : '';
      toast.success(`${editServer.code} pricing updated${varNote}`);
      setEditServer(null); load();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async (svr) => {
    // Deactivation needs confirmation; activation is safe to proceed directly
    if (svr.is_active !== false) { setToggleTarget(svr); return; }
    try {
      const { data } = await adminAPI.toggleServerActive(svr.code);
      toast.success(`${svr.code} ${data.is_active ? 'activated' : 'deactivated'}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to toggle'); }
  };

  const confirmToggle = async () => {
    if (!toggleTarget) return;
    try {
      const { data } = await adminAPI.toggleServerActive(toggleTarget.code);
      toast.success(`${toggleTarget.code} ${data.is_active ? 'activated' : 'deactivated'}`);
      setToggleTarget(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to toggle'); }
  };

  const handleDeleteServer = async (svr) => { setDeleteTarget(svr); };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const { data } = await adminAPI.deleteServer(deleteTarget.code);
      if (data.referenced_quotes > 0) {
        toast.success(`${deleteTarget.code} deleted (referenced in ${data.referenced_quotes} quote(s) - snapshots preserved)`);
      } else {
        toast.success(`${deleteTarget.code} permanently deleted`);
      }
      setDeleteTarget(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleAddServer = async (e) => {
    e.preventDefault();
    setAddSaving(true);
    try {
      await adminAPI.createServer(addForm);
      toast.success(`${addForm.code} created`);
      setShowAdd(false);
      setAddForm(DEFAULT_SERVER);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create server');
    } finally { setAddSaving(false); }
  };

  const tierLabels = useMemo(() => {
    if (!tierBreaks.length) return { '1': 'Tier 1', '2': 'Tier 2', '3': 'Tier 3' };
    const m = {};
    // t.tier_name already contains the user range (e.g. "Tier 1 (20-49 users)")
    // so use it directly - don't append the range again
    tierBreaks.forEach(t => { m[String(t.tier_number)] = t.tier_name; });
    return m;
  }, [tierBreaks]);

  if (loading) return <div className="py-8 space-y-2">{Array.from({length:5}).map((_,i)=><Skeleton key={i} className="h-10 w-full" />)}</div>;

  const inactiveCount = allServers.filter(s => s.server_type === typeFilter && s.is_active === false && !s.is_deleted).length;

  return (
    <div className="space-y-5" data-testid="admin-pricing-server-catalog">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-semibold mb-1" style={{ fontFamily: 'var(--font-display)' }}>Server Catalogue</h3>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
            HANA &amp; SQL upgrade servers support <strong>tier-based</strong> or <strong>single price</strong> mode - set per server in the Pricing tab. App Servers use a flat monthly price per region.
            Inactive servers are hidden from the quote wizard.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {inactiveCount > 0 && (
            <Button variant="outline" size="sm" onClick={() => setShowInactive(v => !v)}
              className="gap-1.5 text-xs"
              style={showInactive ? { background: 'hsl(var(--muted))', fontWeight: 600 } : {}}>
              {showInactive ? 'Hide' : `Show ${inactiveCount} inactive`}
            </Button>
          )}
          <Button onClick={() => { setAddForm({ ...DEFAULT_SERVER, server_type: typeFilter }); setShowAdd(true); }}
            style={{ background: 'hsl(var(--c4p-brand-gold))', color: '#1a1a1a' }}
            className="gap-1.5 font-bold" size="sm"
            data-testid="add-server-button">
            <Plus size={13} /> Add Server
          </Button>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
          {[['HANADB', 'HANA DB Servers'], ['SQLDB', 'SQL DB Servers'], ['PRO4GLDB', 'Progress 4GL DB Servers'], ['Application', 'App Servers']].map(([t, label]) => {
            const total = allServers.filter(s => s.server_type === t && s.is_active !== false && !s.is_deleted).length;
            return (
              <button key={t} onClick={() => setTypeFilter(t)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium"
                style={typeFilter === t ? { background: NAVY, color: 'white' } : { background: 'hsl(var(--muted))', color: 'hsl(var(--muted-fg))' }}>
                {t === 'Application' ? <Server size={13} /> : <Database size={13} />}
                {label} <span className="opacity-60 text-xs">({total})</span>
              </button>
            );
          })}
      </div>
      {typeFilter === 'Application' && (
        <div className="flex gap-2 flex-wrap">
          {['Windows', 'Linux', 'Ubuntu Pro', 'GPU'].map(os => (
            <button key={os} onClick={() => setOsFilter(os)}
              className="px-3 py-1 rounded-md text-xs font-medium border"
              style={osFilter === os ? { background: 'hsl(var(--accent))', color: NAVY, borderColor: NAVY } : { background: 'white', color: 'hsl(var(--muted-fg))', borderColor: 'hsl(var(--border))' }}>
              {os}
            </button>
          ))}
        </div>
      )}
      <div className="rounded-lg p-3 text-xs" style={{ background: 'hsl(var(--accent))', color: NAVY }}>
        {typeFilter === 'HANADB' && <><span>&#9733; CLO-HANA-SVR-MO-16/128 (128GB) is included in Advanced PUPM &mdash; always &pound;0. Set <strong>tier-based</strong> or <strong>single price</strong> for 256GB, 384GB and 512GB upgrades using the Pricing tab on each server.</span></>}
        {typeFilter === 'SQLDB'  && <><span>&#9733; CLO-ULSQL-SVR-MO-4/32 (4vCPU/32GB) is included in Advanced PUPM &mdash; always &pound;0. Set <strong>tier-based</strong> or <strong>single price</strong> for SQL server upgrades using the Pricing tab on each server.</span></>}
        {typeFilter === 'PRO4GLDB' && <><span>All Progress 4GL sizes are priced <strong>upgrades</strong> &mdash; Progress 4GL only appears on C4P Custom quotes, which have no bundled/included server. Set <strong>tier-based</strong> or <strong>single price</strong> mode using the Pricing tab on each server; Custom Quotes themselves are charged from the <strong>Region &times; Currency</strong> price set further down that same tab.</span></>}
        {typeFilter === 'Application' && <><span>App Server pricing uses a <strong>Region &times; Currency matrix</strong> (8 regions &times; 5 currencies = 40 fixed prices per server). The table shows the <strong>London/GBP</strong> reference price and the GBP range across all regions. Click the pencil to edit the full matrix.</span></>}
      </div>
      <div className="rounded-xl border bg-white overflow-hidden">
        <ScrollArea className="w-full">
          <Table>
            <TableHeader>
              <TableRow style={{ background: 'hsl(var(--muted))' }}>
                <TableHead className="min-w-[200px]">Server</TableHead>
                <TableHead>CPU</TableHead><TableHead>RAM</TableHead>
                {typeFilter !== 'Application' && <><TableHead>Data incl.</TableHead><TableHead>Backup incl.</TableHead></>}
                {typeFilter === 'Application' && <><TableHead>OS</TableHead><TableHead>Root incl.</TableHead></>}
                <TableHead>Status</TableHead>
                {typeFilter === 'Application'
                  ? <TableHead>Pricing</TableHead>
                  : <><TableHead>Mode</TableHead><TableHead>Price (GBP / EUR)</TableHead></>
                }
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleServers.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-8" style={{ color: 'hsl(var(--muted-fg))' }}>No servers found.</TableCell></TableRow>
              ) : visibleServers.map(svr => {
                const inactive = svr.is_active === false;
                return (
                  <TableRow key={svr.code} className="data-table-row"
                    style={inactive ? { opacity: 0.45 } : isBase(svr) ? { background: 'hsl(24 95% 98%)' } : {}}>
                    <TableCell>
                      <div className="font-mono-c4p text-xs font-semibold truncate max-w-[190px]" title={svr.code}
                           style={inactive ? { textDecoration: 'line-through' } : {}}>
                        {svr.code}
                      </div>
                      <div className="text-xs truncate max-w-[190px]" style={{ color: 'hsl(var(--muted-fg))' }}>{svr.description}</div>
                    </TableCell>
                    <TableCell className="text-sm tabular-nums">{svr.cpu}v</TableCell>
                    <TableCell className="text-sm tabular-nums">{svr.memory_gb}GB</TableCell>
                    {typeFilter !== 'Application' && (
                      <><TableCell className="text-sm tabular-nums">{svr.included_data_gb}GB</TableCell>
                        <TableCell className="text-sm tabular-nums">{svr.included_backup_gb}GB</TableCell></>
                    )}
                    {typeFilter === 'Application' && (
                      <><TableCell className="text-xs">{svr.os_included}</TableCell>
                        <TableCell className="text-sm tabular-nums">{svr.included_root_gb}GB</TableCell></>
                    )}
                    <TableCell>
                      {inactive
                        ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>Inactive</span>
                        : isBase(svr)
                          ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: 'hsl(var(--c4p-brand-gold) / 0.15)', color: 'hsl(var(--c4p-brand-gold))' }}>&#9733; Included</span>
                          : <span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Upgrade</span>
                      }
                    </TableCell>
                    {typeFilter === 'Application'
                      ? (() => {
                          const rpm = svr.region_price_matrix || {};
                          const hasRegional = Object.keys(rpm).length > 0;
                          const londonGBP = rpm['eu-west-2']?.GBP;
                          const usUSD    = rpm['us-east-1']?.USD;
                          const allGBP   = Object.values(rpm).map(r => r.GBP || 0).filter(v => v > 0);
                          const minGBP   = allGBP.length ? Math.min(...allGBP) : 0;
                          const maxGBP   = allGBP.length ? Math.max(...allGBP) : 0;
                          if (isBase(svr)) return <TableCell><span className="text-xs" style={{ color: '#15803d' }}>Included at £0</span></TableCell>;
                          if (!hasRegional) return <TableCell><span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>&mdash; Not priced</span></TableCell>;
                          return (
                            <TableCell>
                              <div className="space-y-1">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-xs font-bold" style={{ color: NAVY }}>
                                    {londonGBP ? `£${londonGBP}` : '-'}
                                  </span>
                                  {usUSD && <span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>&middot; ${usUSD}</span>}
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
                                    style={{ background: 'hsl(196 75% 56% / 0.12)', color: 'hsl(var(--c4p-brand-blue))' }}>
                                    Regional
                                  </span>
                                  {minGBP !== maxGBP && (
                                    <span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                                      £{minGBP}&ndash;£{maxGBP}/mo
                                    </span>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                          );
                        })()
                      : (
                        <>
                          {/* Mode badge */}
                          <TableCell>
                            {!isBase(svr) && (
                              <span className="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
                                style={svr.pricing_mode === 'static'
                                  ? { background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' }
                                  : { background: 'hsl(196 75% 56% / 0.12)', color: 'hsl(var(--c4p-brand-blue))', border: '1px solid hsl(196 75% 56% / 0.3)' }
                                }>
                                {svr.pricing_mode === 'static' ? 'Single' : 'Tiered'}
                              </span>
                            )}
                          </TableCell>
                          {/* Price cell - static shows flat price, tiered shows matrix */}
                          <TableCell>
                            {isBase(svr) ? <span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Included</span>
                              : svr.pricing_mode === 'static' ? (
                                  <div className="space-y-0.5">
                                    {(svr.prices_by_currency?.GBP > 0 || svr.prices_by_currency?.EUR > 0)
                                      ? <div className="text-xs tabular-nums font-semibold" style={{ color: NAVY }}>
                                          &pound;{(svr.prices_by_currency?.GBP||0).toFixed(0)} / &euro;{(svr.prices_by_currency?.EUR||0).toFixed(0)}
                                        </div>
                                      : <span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Not set</span>
                                    }
                                  </div>
                                ) : (
                                  <div className="space-y-0.5">
                                    {['1','2','3'].map(tier => {
                                      const tp = svr.price_matrix?.[tier] || {};
                                      const gbp = tp.GBP || 0;
                                      return gbp > 0
                                        ? <div key={tier} className="text-xs tabular-nums">T{tier}: &pound;{gbp.toFixed(0)} / &euro;{(tp.EUR||0).toFixed(0)}</div>
                                        : <div key={tier} className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>T{tier}: &mdash;</div>;
                                    })}
                                  </div>
                                )
                            }
                          </TableCell>
                        </>
                        )
                    }
                    {/* Actions: edit + toggle active + delete (base servers can be edited but not deactivated/deleted) */}
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(svr)}
                          title="Edit server details and pricing"
                          data-testid={`edit-server-${svr.code.replace(/\//g, '-')}`}>
                          <Pencil size={13} />
                        </Button>
                        {!isBase(svr) && (
                          <Button variant="ghost" size="sm"
                            onClick={() => handleToggleActive(svr)}
                            title={inactive ? 'Re-activate server' : 'Mark inactive (hides from quote wizard)'}
                            style={{ color: inactive ? '#16a34a' : '#d97706' }}
                            data-testid={`toggle-server-${svr.code.replace(/\//g, '-')}`}>
                            {inactive ? <CheckCircle2 size={13} /> : <EyeOff size={13} />}
                          </Button>
                        )}
                        {!isBase(svr) && (
                          <Button variant="ghost" size="sm"
                            onClick={() => handleDeleteServer(svr)}
                            title="Permanently delete this server"
                            style={{ color: '#dc2626' }}
                            data-testid={`delete-server-${svr.code.replace(/\//g, '-')}`}>
                            <Trash2 size={13} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* ── Deactivate Server Confirmation ── */}
      <Dialog open={!!toggleTarget} onOpenChange={() => setToggleTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle size={16} style={{ color: '#d97706' }} /> Deactivate Server
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
            Deactivate <strong>{toggleTarget?.code}</strong>? It will be hidden from the quote wizard but existing quotes are not affected.
          </p>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setToggleTarget(null)}>Cancel</Button>
            <Button onClick={confirmToggle} style={{ background: '#d97706', color: 'white' }}>Deactivate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Server Confirmation ── */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 size={16} style={{ color: 'hsl(var(--danger))' }} /> Permanently Delete Server
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
            Permanently delete <strong>{deleteTarget?.code}</strong>? This cannot be undone.
            Existing quotes that reference this server will retain their saved pricing snapshot.
          </p>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={confirmDelete} style={{ background: 'hsl(var(--danger))', color: 'white' }}>Delete Server</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add Server Dialog ── */}
      {showAdd && (
        <Dialog open onOpenChange={() => setShowAdd(false)}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>Add New Server</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddServer} className="space-y-4">
              {/* Server Type */}
              <div className="space-y-1">
                <Label>Server Type *</Label>
                <div className="flex gap-2">
                  {['HANADB', 'SQLDB', 'PRO4GLDB', 'Application'].map(t => (
                    <button key={t} type="button"
                      className="flex-1 py-2 rounded-lg text-sm font-semibold border"
                      style={addForm.server_type === t ? { background: NAVY, color: 'white', border: 'none' } : { background: 'white', color: 'hsl(var(--muted-fg))', borderColor: 'hsl(var(--border))' }}
                      onClick={() => setAddForm(f => ({ ...f, server_type: t, os_included: t === 'HANADB' ? 'SUSE Linux Enterprise' : (t === 'SQLDB' || t === 'PRO4GLDB') ? 'Windows' : f.os_included }))}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              {/* Code + Description */}
              <div className="grid grid-cols-1 gap-3">
                <div className="space-y-1">
                  <Label>Item Code * <span className="text-xs font-normal" style={{ color: 'hsl(var(--muted-fg))' }}>e.g. CLO-HANA-SVR-MO-32/256</span></Label>
                  <Input value={addForm.code} onChange={e => setAddForm(f => ({ ...f, code: e.target.value }))} required placeholder="CLO-TYPE-SVR-XX/YY" />
                </div>
                <div className="space-y-1">
                  <Label>Description *</Label>
                  <Input value={addForm.description} onChange={e => setAddForm(f => ({ ...f, description: e.target.value }))} required placeholder="HANA Server - MO 32vCPU/256GB" />
                </div>
              </div>
              {/* CPU / Memory / AWS */}
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>CPU (vCPU) *</Label>
                  <Input type="number" min="1" value={addForm.cpu || ''} onChange={e => setAddForm(f => ({ ...f, cpu: parseInt(e.target.value) || 0 }))} required />
                </div>
                <div className="space-y-1">
                  <Label>Memory (GB) *</Label>
                  <Input type="number" min="1" value={addForm.memory_gb || ''} onChange={e => setAddForm(f => ({ ...f, memory_gb: parseInt(e.target.value) || 0 }))} required />
                </div>
                <div className="space-y-1">
                  <Label>Sort Order</Label>
                  <Input type="number" min="0" value={addForm.sort_order} onChange={e => setAddForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 99 }))} />
                </div>
              </div>
              {/* AWS Instance + OS - internal reference fields */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>AWS Instance (internal) *</Label>
                  <Input value={addForm.aws_instance} onChange={e => setAddForm(f => ({ ...f, aws_instance: e.target.value }))} required placeholder="r7i.8xlarge" />
                  <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Not shown to users - internal reference only</p>
                </div>
                <div className="space-y-1">
                  <Label>OS Included *</Label>
                  <Input value={addForm.os_included} onChange={e => setAddForm(f => ({ ...f, os_included: e.target.value }))} required placeholder="Windows / SUSE Linux Enterprise" />
                </div>
              </div>
              {/* Storage fields */}
              {addForm.server_type !== 'Application' && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Included Data Storage (GB)</Label>
                    <Input type="number" min="0" value={addForm.included_data_gb || ''} onChange={e => setAddForm(f => ({ ...f, included_data_gb: parseInt(e.target.value) || 0 }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>Included Backup Storage (GB)</Label>
                    <Input type="number" min="0" value={addForm.included_backup_gb || ''} onChange={e => setAddForm(f => ({ ...f, included_backup_gb: parseInt(e.target.value) || 0 }))} />
                  </div>
                </div>
              )}
              {addForm.server_type === 'Application' && (
                <div className="space-y-1">
                  <Label>Included Root Volume (GB)</Label>
                  <Input type="number" min="0" value={addForm.included_root_gb || ''} onChange={e => setAddForm(f => ({ ...f, included_root_gb: parseInt(e.target.value) || 100 }))} />
                  <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Standard is 100 GB per server.</p>
                </div>
              )}
              <p className="text-xs rounded-lg p-3" style={{ background: 'hsl(var(--accent))', color: 'hsl(var(--c4p-navy-900))' }}>
                Prices will be set to &pound;0 by default. Use the <strong>Edit Prices</strong> button after creating to set pricing.
              </p>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
                <Button type="submit" disabled={addSaving} style={{ background: NAVY, color: 'white' }}>
                  {addSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus size={14} />} Create Server
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}


      {editServer && (
        <Dialog open onOpenChange={() => setEditServer(null)}>
          <DialogContent className="max-w-3xl overflow-y-auto" style={{ maxHeight: 'min(98vh, 900px)' }}>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>
                Edit Server - {editServer.description}
              </DialogTitle>
              <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{editServer.code}</p>
            </DialogHeader>

            <Tabs defaultValue="details">
              <TabsList className="mb-4">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="pricing">Pricing</TabsTrigger>
              </TabsList>

              {/* ── Details Tab ── */}
              <TabsContent value="details" className="space-y-4 mt-0">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Item Code *</Label>
                    <Input value={editDetails.code || ''} onChange={e => setEditDetails(d => ({ ...d, code: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Description *</Label>
                    <Input value={editDetails.description || ''} onChange={e => setEditDetails(d => ({ ...d, description: e.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>AWS Instance (internal)</Label>
                    <Input value={editDetails.aws_instance || ''} onChange={e => setEditDetails(d => ({ ...d, aws_instance: e.target.value }))} placeholder="e.g. r7i.8xlarge" />
                    <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Not shown to users</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>OS Included</Label>
                    <Input value={editDetails.os_included || ''} onChange={e => setEditDetails(d => ({ ...d, os_included: e.target.value }))} placeholder="e.g. Windows" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label>CPU (vCPU)</Label>
                    <Input type="number" min={1} value={editDetails.cpu || ''} onChange={e => setEditDetails(d => ({ ...d, cpu: parseInt(e.target.value) || 0 }))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>RAM (GB)</Label>
                    <Input type="number" min={1} value={editDetails.memory_gb || ''} onChange={e => setEditDetails(d => ({ ...d, memory_gb: parseInt(e.target.value) || 0 }))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Sort Order</Label>
                    <Input type="number" min={0} value={editDetails.sort_order ?? ''} onChange={e => setEditDetails(d => ({ ...d, sort_order: parseInt(e.target.value) || 0 }))} />
                  </div>
                </div>

                {/* DB/App specific storage */}
                {isDbServer(editServer) && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Included Data Storage (GB)</Label>
                      <Input type="number" min={0} value={editDetails.included_data_gb ?? ''} onChange={e => setEditDetails(d => ({ ...d, included_data_gb: parseInt(e.target.value) || 0 }))} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Included Backup Storage (GB)</Label>
                      <Input type="number" min={0} value={editDetails.included_backup_gb ?? ''} onChange={e => setEditDetails(d => ({ ...d, included_backup_gb: parseInt(e.target.value) || 0 }))} />
                    </div>
                  </div>
                )}
                {!isDbServer(editServer) && (
                  <div className="space-y-1.5">
                    <Label>Included Root Volume (GB)</Label>
                    <Input type="number" min={0} value={editDetails.included_root_gb ?? ''} onChange={e => setEditDetails(d => ({ ...d, included_root_gb: parseInt(e.target.value) || 0 }))} />
                  </div>
                )}

                {/* Flags */}
                <div className="rounded-xl border p-4 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>Flags</p>
                  {editServer.server_type === 'PRO4GLDB' && (
                    <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                      Progress 4GL sizes are always priced upgrades on C4P Custom quotes &mdash; there's no included/base flag for this database type.
                    </p>
                  )}
                  {isDbServer(editServer) && editServer.server_type !== 'PRO4GLDB' && (
                    <>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Included as Base HANA Server</p>
                          <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Shown at £0 in Advanced &amp; MTPro quotes - toggle to reassign the included base server</p>
                        </div>
                        <Switch checked={!!editDetails.is_base_for_hana} onCheckedChange={v => setEditDetails(d => ({ ...d, is_base_for_hana: v }))} />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Included as Base SQL Server</p>
                          <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Shown at £0 in Advanced &amp; MTPro quotes - toggle to reassign the included base server</p>
                        </div>
                        <Switch checked={!!editDetails.is_base_for_sql} onCheckedChange={v => setEditDetails(d => ({ ...d, is_base_for_sql: v }))} />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">SQL Server Included</p>
                          <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>SQL Server licence is included with this server</p>
                        </div>
                        <Switch checked={!!editDetails.sql_server_included} onCheckedChange={v => setEditDetails(d => ({ ...d, sql_server_included: v }))} />
                      </div>
                    </>
                  )}
                  {!isDbServer(editServer) && (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">Included App Server (Advanced)</p>
                        <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Auto-included at £0 in C4P Advanced quotes</p>
                      </div>
                      <Switch checked={!!editDetails.is_included_app_server} onCheckedChange={v => setEditDetails(d => ({ ...d, is_included_app_server: v }))} />
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditServer(null)}>Cancel</Button>
                  <Button onClick={saveDetails} disabled={savingDetails} style={{ background: NAVY, color: 'white' }} className="gap-2">
                    {savingDetails ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={14} />} Save Details
                  </Button>
                </DialogFooter>
              </TabsContent>

              {/* ── Pricing Tab ── */}
              <TabsContent value="pricing" className="space-y-4 mt-0">
                {isDbServer(editServer) ? (
                  <div className="space-y-3">
                    {/* Pricing mode toggle */}
                    <div className="flex items-center gap-3 rounded-lg p-3" style={{ background: 'hsl(var(--muted))' }}>
                      <div className="flex-1">
                        <p className="text-sm font-semibold">Pricing Mode</p>
                        <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
                          {editPricingMode === 'tiered'
                            ? 'Price varies by user-count tier - lower rates for higher user bands.'
                            : 'Single fixed price regardless of user count.'}
                        </p>
                      </div>
                      <div className="flex rounded-lg overflow-hidden border">
                        {[['tiered', 'Tier-based'], ['static', 'Single price']].map(([mode, label]) => (
                          <button key={mode} type="button" onClick={() => setEditPricingMode(mode)}
                            className="px-3 py-1.5 text-xs font-semibold transition-colors"
                            style={editPricingMode === mode
                              ? { background: mode === 'static' ? ORANGE : NAVY, color: 'white' }
                              : { background: 'white', color: 'hsl(var(--muted-fg))' }}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Tier-based matrix */}
                    {editPricingMode === 'tiered' && (
                      <div className="rounded-lg border overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow style={{ background: 'hsl(var(--muted))' }}>
                              <TableHead>User Tier</TableHead>
                              {CURRENCIES.map(c => <TableHead key={c}>{CURR_SYMBOLS[c]} {c} /mo</TableHead>)}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {['1','2','3'].map(tier => (
                              <TableRow key={tier}>
                                <TableCell className="font-medium text-sm w-36">
                                  <div>{tierLabels[tier] || `Tier ${tier}`}</div>
                                </TableCell>
                                {CURRENCIES.map(c => (
                                  <TableCell key={c}>
                                    <Input type="number" min="0" step="0.01"
                                      value={editMatrix[tier]?.[c] || ''}
                                      onChange={e => setEditMatrix(m => ({ ...m, [tier]: { ...(m[tier]||{}), [c]: parseFloat(e.target.value)||0 } }))}
                                      className="w-24 h-8 text-sm tabular-nums" />
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        <p className="px-3 py-2 text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                          Higher tiers (more users) should be priced lower - their larger PUPM contribution offsets the upgrade cost.
                        </p>
                      </div>
                    )}

                    {/* Static single price */}
                    {editPricingMode === 'static' && (
                      <div className="rounded-lg border overflow-hidden">
                        <div className="px-3 py-2 text-xs font-semibold" style={{ background: 'hsl(var(--muted))', color: NAVY, borderBottom: '1px solid hsl(var(--border))' }}>
                          Fixed Monthly Price - all user counts pay the same rate
                        </div>
                        <div className="p-3 space-y-2">
                          {CURRENCIES.map(c => (
                            <div key={c} className="flex items-center gap-3">
                              <span className="w-16 text-sm font-medium tabular-nums">{CURR_SYMBOLS[c]} {c}</span>
                              <Input type="number" min="0" step="0.01" placeholder="0.00"
                                value={editStaticPrices[c] || ''}
                                onChange={e => setEditStaticPrices(p => ({ ...p, [c]: e.target.value }))}
                                className="w-36 tabular-nums h-8 text-sm" />
                              <span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>/month</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* C4P Custom Price - Region x Currency matrix */}
                    <div className="rounded-lg border overflow-hidden">
                      <div className="px-3 py-2 text-xs font-semibold" style={{ background: 'hsl(196 75% 56% / 0.08)', color: 'hsl(var(--c4p-brand-blue))', borderBottom: '1px solid hsl(var(--border))' }}>
                        C4P Custom Price - Region × Currency (full price, no user subsidy)
                      </div>
                      <p className="px-3 py-2 text-xs" style={{ color: 'hsl(var(--muted-fg))', borderBottom: '1px solid hsl(var(--border))' }}>
                        Set per Region × Currency. Each cell is the exact monthly charge for a C4P Custom (Test System / Custom) quote. Overrides the flat fallback below when a region is matched.
                      </p>
                      <div className="overflow-auto" style={{ maxHeight: '300px' }}>
                        <Table>
                          <TableHeader>
                            <TableRow style={{ background: NAVY, color: 'white' }}>
                              <TableHead className="text-white text-xs sticky left-0" style={{ background: NAVY, minWidth: '130px' }}>Region</TableHead>
                              {CURRENCIES.map(c => <TableHead key={c} className="text-xs text-white">{CURR_SYMBOLS[c]} {c}</TableHead>)}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {APP_SERVER_REGIONS.map((region, i) => (
                              <TableRow key={region.code} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                                <TableCell className="text-xs font-medium sticky left-0" style={{ background: 'inherit', minWidth: '130px' }}>{region.name}</TableCell>
                                {CURRENCIES.map(c => (
                                  <TableCell key={c} className="px-2 py-1">
                                    <Input type="number" min="0" step="0.01"
                                      value={editStandaloneMatrix[region.code]?.[c] ?? ''}
                                      onChange={e => setEditStandaloneMatrix(m => ({ ...m, [region.code]: { ...(m[region.code] || {}), [c]: parseFloat(e.target.value) || 0 } }))}
                                      className="w-20 h-7 text-xs tabular-nums" />
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      <div className="px-3 py-2" style={{ borderTop: '1px solid hsl(var(--border))', background: 'hsl(var(--muted) / 0.4)' }}>
                        <p className="text-xs font-semibold mb-2" style={{ color: 'hsl(var(--muted-fg))' }}>Flat fallback (used when no regional price is set)</p>
                        <div className="grid grid-cols-5 gap-2">
                          {CURRENCIES.map(c => (
                            <div key={c} className="space-y-1">
                              <label className="text-xs">{CURR_SYMBOLS[c]} {c}</label>
                              <Input type="number" min="0" step="0.01" placeholder="0.00"
                                value={editStandalonePrices[c] || ''}
                                onChange={e => setEditStandalonePrices(p => ({ ...p, [c]: e.target.value }))}
                                className="h-7 tabular-nums text-xs" />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : editServer && editServer.region_price_matrix && Object.keys(editServer.region_price_matrix).length > 0 ? (
                  /* App Server - region × currency matrix */
                  <div className="space-y-2">
                    <p className="text-xs rounded-lg px-3 py-2" style={{ background: 'hsl(var(--accent))', color: 'hsl(var(--c4p-brand-blue))' }}>
                      Prices are fixed per Region × Currency. Each cell is the exact monthly charge - no FX conversion applied.
                    </p>
                    <div className="rounded-lg border overflow-auto" style={{ maxHeight: '340px' }}>
                      <Table>
                        <TableHeader>
                          <TableRow style={{ background: NAVY, color: 'white' }}>
                            <TableHead className="text-white text-xs sticky left-0" style={{ background: NAVY, minWidth: '130px' }}>Region</TableHead>
                            {CURRENCIES.map(c => <TableHead key={c} className="text-xs">{CURR_SYMBOLS[c]} {c}</TableHead>)}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {APP_SERVER_REGIONS.map((region, i) => (
                            <TableRow key={region.code} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                              <TableCell className="text-xs font-medium sticky left-0" style={{ background: 'inherit', minWidth: '130px' }}>{region.name}</TableCell>
                              {CURRENCIES.map(c => (
                                <TableCell key={c} className="px-2 py-1">
                                  <Input type="number" min="0" step="0.01"
                                    value={editMatrix[region.code]?.[c] ?? ''}
                                    onChange={e => setEditMatrix(m => ({ ...m, [region.code]: { ...(m[region.code]||{}), [c]: parseFloat(e.target.value)||0 } }))}
                                    className="w-20 h-7 text-xs tabular-nums" />
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                ) : (
                  /* App Server without region matrix - flat prices */
                  <div className="space-y-3 py-1">
                    {CURRENCIES.map(c => (
                      <div key={c} className="flex items-center gap-3">
                        <span className="inline-block w-16 text-sm font-semibold text-right flex-shrink-0">{CURR_SYMBOLS[c]} {c}</span>
                        <Input type="number" min="0" step="0.01" value={editPrices[c] ?? ''}
                          onChange={e => setEditPrices(p => ({ ...p, [c]: parseFloat(e.target.value)||0 }))}
                          className="flex-1 tabular-nums" />
                        <span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>/month</span>
                      </div>
                    ))}
                  </div>
                )}
                {editServer && !isDbServer(editServer) && (
                  <div className="flex items-center gap-2 px-1 pb-1">
                    <input type="checkbox" id="apply-variants" checked={applyToVariants}
                      onChange={e => setApplyToVariants(e.target.checked)} className="h-4 w-4 rounded" />
                    <label htmlFor="apply-variants" className="text-xs cursor-pointer select-none" style={{ color: 'hsl(var(--muted-fg))' }}>
                      Apply same prices to all OS variants (Linux, Ubuntu Pro, GPU) of this server
                    </label>
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditServer(null)}>Cancel</Button>
                  <Button onClick={saveServer} disabled={saving} style={{ background: NAVY, color: 'white' }} className="gap-2">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={14} />} Save Prices
                  </Button>
                </DialogFooter>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


// ── Storage Pricing Tab ────────────────────────────────────────────────────────

// ── Add-on Users Tab ───────────────────────────────────────────────────────────
function AddonUsersTab() {
  const [prices, setPrices] = useState({
    web_client_pupm: { GBP: 0, EUR: 0, USD: 0, CAD: 0, AUD: 0, AUD: 0 },
    mobile_user_pupm: { GBP: 0, EUR: 0, USD: 0, CAD: 0, AUD: 0, AUD: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const load = async () => {
    try {
      const { data } = await adminAPI.getAddonUserPricing();
      setPrices({
        web_client_pupm:  { GBP: 0, EUR: 0, USD: 0, CAD: 0, AUD: 0, ...(data.web_client_pupm?.prices_by_currency  || {}) },
        mobile_user_pupm: { GBP: 0, EUR: 0, USD: 0, CAD: 0, AUD: 0, ...(data.mobile_user_pupm?.prices_by_currency || {}) },
      });
    } catch { toast.error('Failed to load add-on user pricing'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveRow = async (configType) => {
    setSaving(s => ({ ...s, [configType]: true }));
    try {
      await adminAPI.updateAddonUserPricing(configType, { prices_by_currency: prices[configType] });
      toast.success(`${configType === 'web_client_pupm' ? 'Web Client User' : 'Mobile User'} pricing saved`);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(s => ({ ...s, [configType]: false })); }
  };

  const ROWS = [
    { key: 'web_client_pupm',  label: 'Web Client User',  desc: 'Flat per-user/month - no tier contribution - applies to Standard, Advanced & MTPro' },
    { key: 'mobile_user_pupm', label: 'Mobile User',       desc: 'Flat per-user/month - no tier contribution - applies to Standard, Advanced & MTPro' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-bold" style={{ color: NAVY }}>Add-on User Types</h3>
        <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
          Web Client Users and Mobile Users are priced at a flat rate per user per month. They do not count towards
          the Advanced tier structure and apply to Standard, Advanced and MTPro products.
        </p>
      </div>
      {loading ? <Skeleton className="h-32 w-full" /> : ROWS.map(row => (
        <div key={row.key} className="rounded-xl border bg-white p-5 space-y-4">
          <div>
            <p className="text-sm font-bold" style={{ color: NAVY }}>{row.label}</p>
            <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>{row.desc}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {CURRENCIES.map(c => (
              <div key={c} className="flex items-center gap-3">
                <span className="inline-block w-16 text-sm font-semibold text-right flex-shrink-0">{CURR_SYMBOLS[c]} {c}</span>
                <Input
                  type="number" min="0" step="0.01"
                  value={prices[row.key][c] ?? ''}
                  onChange={e => setPrices(prev => ({ ...prev, [row.key]: { ...prev[row.key], [c]: parseFloat(e.target.value) || 0 } }))}
                  className="flex-1 tabular-nums"
                />
                <span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>/user/mo</span>
              </div>
            ))}
          </div>
          <Button onClick={() => saveRow(row.key)} disabled={!!saving[row.key]}
            style={{ background: NAVY, color: 'white' }} className="gap-1.5">
            {saving[row.key] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={13} />}
            Save {row.label} Prices
          </Button>
        </div>
      ))}
    </div>
  );
}


function StoragePricingTab() {
  const [storage, setStorage]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [inlinePrices, setInlinePrices] = useState({});
  const [savingInline, setSavingInline] = useState({});

  const load = async () => {
    try {
      const { data } = await adminAPI.getStoragePricing();
      setStorage(data || []);
      const p = {};
      (data || []).forEach(s => { p[s.storage_type] = { ...s.prices_by_currency }; });
      setInlinePrices(p);
    } catch { toast.error('Failed to load storage pricing'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const saveRow = async (storageType) => {
    setSavingInline(s => ({ ...s, [storageType]: true }));
    try {
      await adminAPI.updateStoragePricing(storageType, { prices_by_currency: inlinePrices[storageType] });
      toast.success(`${storageType} pricing saved`);
      load();
    } catch { toast.error('Failed to save'); }
    finally { setSavingInline(s => ({ ...s, [storageType]: false })); }
  };

  const STORAGE_LABELS = {
    root_volume:  { label: 'Root Volume (App Servers)',  desc: 'Root drive per app server', icon: 'App', color: '#0891b2' },
    hana_data:    { label: 'HANA Data Volume',           desc: 'HANA database data storage', icon: 'H', color: '#7c3aed' },
    hana_backup:  { label: 'HANA Backup Volume',         desc: 'HANA backup storage',        icon: 'H', color: '#7c3aed' },
    sql_data:     { label: 'SQL Data Volume',            desc: 'SQL database data storage',  icon: 'S', color: '#16a34a' },
    sql_backup:   { label: 'SQL Backup Volume',          desc: 'SQL backup storage',         icon: 'S', color: '#16a34a' },
    file_volume:  { label: 'File Storage',               desc: 'EBS file storage blocks (Standard: 10GB incl., Advanced/MTPro: 100GB incl.)', icon: 'F', color: '#ea580c' },
  };

  if (loading) return <div className="py-8 space-y-3">{Array.from({length:5}).map((_,i)=><Skeleton key={i} className="h-16 w-full" />)}</div>;

  return (
    <div className="space-y-5" data-testid="admin-pricing-storage">
      <div>
        <h3 className="font-semibold" style={{ fontFamily: 'var(--font-display)' }}>Storage Pricing</h3>
        <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
          Per-100 GB block pricing. Fractional blocks supported (e.g. 50 GB = 0.5 &times; block price).
        </p>
      </div>
      <div className="space-y-3">
        {storage.map(s => {
          const meta = STORAGE_LABELS[s.storage_type] || { label: s.storage_type, desc: '', icon: 'S', color: '#64748b' };
          const rp = inlinePrices[s.storage_type] || {};
          const isSaving = savingInline[s.storage_type];
          return (
            <div key={s.storage_type} className="rounded-xl border bg-white overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-3" style={{ borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--muted))' }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: meta.color }}>{meta.icon}</div>
                <div className="flex-1">
                  <p className="font-semibold text-sm">{meta.label}</p>
                  <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{meta.desc} &middot; per 100 GB block</p>
                </div>
                <Button onClick={() => saveRow(s.storage_type)} disabled={isSaving} size="sm"
                  style={{ background: NAVY, color: 'white' }} className="gap-1.5"
                  data-testid={`save-storage-${s.storage_type}`}>
                  {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save size={12} />} Save
                </Button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 px-5 py-4">
                {CURRENCIES.map(c => (
                  <div key={c} className="space-y-1">
                    <Label className="text-xs">{CURR_SYMBOLS[c]} {c} / 100GB</Label>
                    <Input type="number" min="0" step="0.01"
                      value={rp[c] ?? ''}
                      onChange={e => setInlinePrices(prev => ({ ...prev, [s.storage_type]: { ...prev[s.storage_type], [c]: parseFloat(e.target.value) || 0 } }))}
                      className="tabular-nums"
                      data-testid={`storage-price-${s.storage_type}-${c}`} />
                    {rp[c] > 0 && <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>50GB = {CURR_SYMBOLS[c]}{(rp[c] * 0.5).toFixed(2)}</p>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Extras Catalogue Tab ─────────────────────────────────────────────────────────
const defaultExtra = { code: '', name: '', description: '', unit: 'each', pricing_type: 'recurring', applies_to: ['STANDARD', 'ADVANCED', 'MTPRO'], prices_by_currency: { GBP: 0, EUR: 0, USD: 0, CAD: 0, AUD: 0, AUD: 0 }, sort_order: 99 };

function ExtrasTab() {
  const [extras, setExtras]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editExtra, setEditExtra] = useState(null);
  const [form, setForm]         = useState(defaultExtra);
  const [saving, setSaving]     = useState(false);
  const [isNew, setIsNew]       = useState(false);

  const load = () => adminAPI.getExtras().then(r => { setExtras(r.data || []); setLoading(false); }).catch(() => toast.error('Failed'));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openEdit = (extra, newItem = false) => { setIsNew(newItem); setEditExtra(extra); setForm(newItem ? { ...defaultExtra } : { ...extra, prices_by_currency: { ...extra.prices_by_currency } }); };

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) { await adminAPI.createExtra(form); toast.success('Extra created'); }
      else { await adminAPI.updateExtra(editExtra.id, form); toast.success('Extra updated'); }
      setEditExtra(null); load();
    } catch (ex) { toast.error(ex.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const deactivate = async (extra) => {
    try { await adminAPI.deleteExtra(extra.id); toast.success('Deactivated'); load(); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <div className="py-8"><Skeleton className="h-48 w-full" /></div>;
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold" style={{ fontFamily: 'var(--font-display)' }}>Extras Catalogue</h3>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>VPN, file storage, additional DB instances. Recurring extras are billed monthly.</p>
        </div>
        <Button onClick={() => openEdit(null, true)} style={{ background: 'hsl(var(--c4p-brand-gold))', color: '#1a1a1a' }} className="gap-2" data-testid="add-extra-button">
          <Plus size={14} /> Add Extra
        </Button>
      </div>
      <div className="rounded-xl border bg-white overflow-hidden">
        <ScrollArea className="w-full">
          <Table>
            <TableHeader><TableRow style={{ background: 'hsl(var(--muted))' }}>
              <TableHead>Name</TableHead><TableHead>Unit</TableHead><TableHead>Type</TableHead><TableHead>Applies To</TableHead>
              {CURRENCIES.map(c => <TableHead key={c}>{CURR_SYMBOLS[c]} {c}</TableHead>)}
              <TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {extras.map(e => (
                <TableRow key={e.id} className="data-table-row">
                  <TableCell className="font-medium text-sm max-w-[160px] truncate">{e.name}</TableCell>
                  <TableCell className="text-sm">{e.unit}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{e.pricing_type}</Badge></TableCell>
                  <TableCell className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{(e.applies_to || []).join(' / ')}</TableCell>
                  {CURRENCIES.map(c => <TableCell key={c} className="tabular-nums text-sm">{CURR_SYMBOLS[c]}{(e.prices_by_currency?.[c] || 0).toFixed(2)}</TableCell>)}
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(e)}><Pencil size={12} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => deactivate(e)}><Trash2 size={12} style={{ color: 'hsl(var(--danger))' }} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>
      <Dialog open={!!editExtra || isNew} onOpenChange={() => { setEditExtra(null); setIsNew(false); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{isNew ? 'Add Extra' : `Edit - ${editExtra?.name}`}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Item Code</Label><Input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} /></div>
              <div className="space-y-1"><Label>Name</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            </div>
            <div className="space-y-1"><Label>Description</Label><Input value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Unit</Label>
                <select value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} className="w-full h-9 rounded-md border px-3 text-sm">
                  {['each','GB','block','month'].map(u => <option key={u} value={u}>{u}</option>)}
                </select></div>
              <div className="space-y-1"><Label>Pricing Type</Label>
                <select value={form.pricing_type} onChange={e => setForm(f => ({ ...f, pricing_type: e.target.value }))} className="w-full h-9 rounded-md border px-3 text-sm">
                  <option value="recurring">Recurring (&times; term)</option>
                  <option value="one_off">One-off (flat fee)</option>
                </select></div>
            </div>
            <div className="space-y-2">
              <Label>Prices per Currency</Label>
              <div className="grid grid-cols-2 gap-3">
                {CURRENCIES.map(c => (
                  <div key={c} className="flex items-center gap-2">
                    <span className="w-10 text-sm font-medium">{CURR_SYMBOLS[c]} {c}</span>
                    <Input type="number" min="0" step="0.01" value={form.prices_by_currency?.[c] ?? ''}
                      onChange={e => setForm(f => ({ ...f, prices_by_currency: { ...f.prices_by_currency, [c]: parseFloat(e.target.value) || 0 } }))}
                      className="tabular-nums h-8" />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditExtra(null); setIsNew(false); }}>Cancel</Button>
            <Button onClick={save} disabled={saving} style={{ background: NAVY, color: 'white' }}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (isNew ? 'Create Extra' : 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ── Standalone PUPM Tab ──────────────────────────────────────────────────────
function StandaloneTab() {
  const [data, setData] = useState(null);
  const [prices, setPrices] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminAPI.getStandalonePupm().then(r => { setData(r.data); setPrices(r.data?.prices_by_currency || {}); })
      .catch(() => toast.error('Failed to load'));
  }, []);

  const save = async () => {
    setSaving(true);
    try { await adminAPI.updateStandalonePupm({ prices_by_currency: prices }); toast.success('Saved'); }
    catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="py-8"><Skeleton className="h-32 w-full" /></div>;
  return (
    <div className="space-y-5" data-testid="admin-pricing-standalone-table">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold" style={{ fontFamily: 'var(--font-display)' }}>Standalone PUPM - Test System &amp; Bespoke</h3>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
            Flat per-user rate for Standalone quotes. No tier breaks, no minimum users. Zero users allowed (system borrows users from Production).
          </p>
        </div>
        <Button onClick={save} disabled={saving} style={{ background: NAVY, color: 'white' }} className="gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={14} />} Save
        </Button>
      </div>
      <div className="rounded-xl border bg-white overflow-hidden">
        <Table>
          <TableHeader><TableRow style={{ background: 'hsl(var(--muted))' }}>
            <TableHead>Currency</TableHead>
            <TableHead>PUPM (flat rate - no tiers)</TableHead>
            <TableHead style={{ color: 'hsl(var(--muted-fg))' }}>Example: 5 users</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {CURRENCIES.map(c => (
              <TableRow key={c}>
                <TableCell className="font-semibold">{CURR_SYMBOLS[c]} {c}</TableCell>
                <TableCell>
                  <Input type="number" min="0" step="0.01" value={prices[c] ?? ''}
                    onChange={e => setPrices(p => ({ ...p, [c]: parseFloat(e.target.value) || 0 }))}
                    className="w-32 tabular-nums" />
                </TableCell>
                <TableCell className="tabular-nums text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
                  {prices[c] ? `${CURR_SYMBOLS[c]}${(prices[c] * 5).toFixed(2)}/mo` : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="rounded-lg p-3 text-sm" style={{ background: 'hsl(var(--accent))' }}>
        <p style={{ color: NAVY }}>
          <strong>Standalone DB Server prices</strong> are set per-server in the <strong>Server Catalogue</strong> tab - look for the "Standalone Price" section in each DB server's edit dialog.
        </p>
      </div>
    </div>
  );
}


// ── Main PricingAdmin ─────────────────────────────────────────────────────────
// ── Item Codes Tab ────────────────────────────────────────────────────────────
function ItemCodesTab() {
  const LABELS = {
    standard:             { label: 'C4P Standard',                hint: 'Base user licence - Per User Per Month (Standard product)' },
    advanced:             { label: 'C4P Advanced',                hint: 'Base user licence - Per User Per Month (Advanced product, all tier breaks share one code)' },
    mtpro:                { label: 'C4P MTPro',                   hint: 'Base user licence - Per User Per Month (MTPro product, all tier breaks share one code)' },
    standalone:           { label: 'C4P Custom (Standalone)',     hint: 'User licence for Test System / Bespoke / Custom quotes' },
    web_client:           { label: 'Web Client Add-on User',      hint: 'Flat per-user add-on - Web Client users' },
    mobile:               { label: 'Mobile Add-on User',          hint: 'Flat per-user add-on - Mobile users' },
    storage_root:         { label: 'Root Drive Storage (100GB)',  hint: 'Additional root drive storage - all server types (per 100GB block)' },
    storage_hana_data:    { label: 'HANA Data Volume (100GB)',    hint: 'Additional HANA data volume storage (per 100GB block)' },
    storage_hana_backup:  { label: 'HANA Backup Volume (100GB)', hint: 'Additional HANA backup volume storage (per 100GB block)' },
    storage_sql_data:     { label: 'SQL Data Volume (100GB)',     hint: 'Additional SQL Server data volume storage (per 100GB block)' },
    storage_sql_backup:   { label: 'SQL Backup Volume (100GB)',   hint: 'Additional SQL Server backup volume storage (per 100GB block)' },
    storage_file:         { label: 'File Storage (100GB)',        hint: 'Additional file storage - beyond included allowance (per 100GB block)' },
  };

  const [codes, setCodes] = useState({ standard: '', advanced: '', mtpro: '', standalone: '', web_client: '', mobile: '', storage_root: '', storage_hana_data: '', storage_hana_backup: '', storage_sql_data: '', storage_sql_backup: '', storage_file: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.getItemCodes()
      .then(r => { if (r.data) setCodes(r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.saveItemCodes(codes);
      toast.success('Item codes saved — new quotes will include these codes');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { toast.error('Failed to save item codes'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="space-y-3">{[1,2,3,4,5,6,7].map(i => <div key={i} className="h-14 rounded-lg bg-gray-100 animate-pulse" />)}</div>;

  return (
    <div className="space-y-6" data-testid="admin-item-codes-tab">
      <div className="rounded-xl border bg-white p-5 space-y-4">
        <div>
          <h3 className="text-sm font-bold" style={{ color: NAVY }}>Product Item Codes</h3>
          <p className="text-xs mt-1" style={{ color: 'hsl(var(--muted-fg))' }}>
            Item codes appear on PDF quotes and the customer signing page as a small reference below each line item.
            Leave blank if not applicable. Servers and Extras use their own built-in codes.
            Changes apply to new quotes immediately - existing saved quotes retain the codes baked in at save time.
          </p>
        </div>
        <div className="space-y-4">
          {Object.entries(LABELS).map(([key, { label, hint }]) => (
            <div key={key} className="grid grid-cols-[1fr_220px] gap-4 items-start py-3 border-b last:border-b-0" style={{ borderColor: 'hsl(var(--border) / 0.5)' }}>
              <div>
                <p className="text-sm font-semibold" style={{ color: NAVY }}>{label}</p>
                <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>{hint}</p>
              </div>
              <Input
                value={codes[key] || ''}
                onChange={e => setCodes(c => ({ ...c, [key]: e.target.value }))}
                placeholder={`e.g. CLO-${key.toUpperCase().replace('_', '-')}`}
                className="font-mono text-sm h-9"
                data-testid={`item-code-${key}`}
              />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 pt-2">
          <Button onClick={handleSave} disabled={saving} style={{ background: NAVY, color: 'white' }} className="gap-2">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save Item Codes
          </Button>
          {saved && <span className="flex items-center gap-1.5 text-sm font-medium" style={{ color: '#15803d' }}><CheckCircle2 size={14} /> Saved</span>}
        </div>
      </div>
    </div>
  );
}

export default function PricingAdmin() {
  return (
    <div className="space-y-5" data-testid="admin-pricing-page">
      <div>
        <h2 className="text-xl font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>Pricing Configuration</h2>
        <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>All pricing configurable without code changes. Changes apply to new quotes immediately.</p>
      </div>
      <Tabs defaultValue="standard">
        <TabsList className="mb-4 flex-wrap h-auto gap-1">
          <TabsTrigger value="standard">Standard PUPM</TabsTrigger>
          <TabsTrigger value="advanced">Advanced Tiers</TabsTrigger>
          <TabsTrigger value="addon">Add-on Users</TabsTrigger>
          <TabsTrigger value="standalone">Standalone PUPM</TabsTrigger>
          <TabsTrigger value="servers">Server Catalogue</TabsTrigger>
          <TabsTrigger value="storage">Storage Pricing</TabsTrigger>
          <TabsTrigger value="extras">Extras Catalogue</TabsTrigger>
          <TabsTrigger value="item-codes">Item Codes</TabsTrigger>
        </TabsList>
        <TabsContent value="standard"><StandardTab /></TabsContent>
        <TabsContent value="advanced"><AdvancedTiersTab /></TabsContent>
        <TabsContent value="addon"><AddonUsersTab /></TabsContent>
        <TabsContent value="standalone"><StandaloneTab /></TabsContent>
        <TabsContent value="servers"><ServerCatalogTab /></TabsContent>
        <TabsContent value="storage"><StoragePricingTab /></TabsContent>
        <TabsContent value="extras"><ExtrasTab /></TabsContent>
        <TabsContent value="item-codes"><ItemCodesTab /></TabsContent>
      </Tabs>
    </div>
  );
}
